import { PropertyValue } from '@affine/component';
import { ConfigModal } from '@affine/core/components/mobile';
import { DefaultInlineManagerExtension } from '@blocksuite/affine/inlines/preset';
import { RichText } from '@blocksuite/affine/rich-text';
import type { BlockStdScope } from '@blocksuite/affine/std';
import { Text, type Store } from '@blocksuite/affine/store';
import { TextIcon } from '@blocksuite/icons/rc';
import { type LiveData, useLiveData } from '@toeverything/infra';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type * as Y from 'yjs';

import type { DatabaseCellRendererProps } from '../../../types';
import { useBlockStdScope } from '../../../use-std';
import * as styles from './rich-text.css';

// todo(@pengx17): handle markdown/keyboard shortcuts
const renderRichText = ({
  doc,
  std,
  text,
}: {
  std: BlockStdScope;
  text: Y.Text;
  doc: Store;
}) => {
  const inlineManager = std.get(DefaultInlineManagerExtension.identifier);

  if (!inlineManager) {
    return null;
  }

  const richText = new RichText();
  richText.yText = text;
  richText.undoManager = doc.history.undoManager;
  richText.readonly = doc.readonly;
  richText.attributesSchema = inlineManager.getSchema() as any;
  richText.attributeRenderer = inlineManager.getRenderer();
  return richText;
};

const RichTextInput = ({
  cell,
  dataSource,
  onChange,
  rowId,
  style,
}: DatabaseCellRendererProps & { style?: CSSProperties }) => {
  const std = useBlockStdScope(dataSource.doc);
  const cellValue = useLiveData(cell.value$ as LiveData<Y.Text | Text | undefined>);
  const ref = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Create or get the Y.Text for this cell
  // If the cell value is undefined, we need to initialize it with a new Text
  const text = useMemo(() => {
    if (cellValue instanceof Text) {
      return cellValue.yText;
    }
    if (cellValue) {
      return cellValue;
    }
    return undefined;
  }, [cellValue]);

  // Initialize the cell with a new Text if it's empty
  const initializeCellIfNeeded = useCallback(() => {
    if (!text && !initializedRef.current) {
      initializedRef.current = true;
      const newText = new Text('');
      dataSource.cellValueChange(rowId, cell.property.id, newText);
    }
  }, [text, dataSource, rowId, cell.property.id]);

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = '';

      // If no text, show a placeholder and initialize on focus
      if (!text) {
        const placeholder = document.createElement('div');
        placeholder.className = styles.placeholder;
        placeholder.textContent = 'Click to edit...';
        placeholder.style.color = 'var(--affine-text-placeholder-color)';
        placeholder.style.cursor = 'text';
        placeholder.onclick = () => {
          initializeCellIfNeeded();
        };
        ref.current.append(placeholder);
        return () => {
          placeholder.remove();
        };
      }

      const richText = renderRichText({ doc: dataSource.doc, std, text });
      const listener = () => {
        onChange(text);
      };
      if (richText) {
        richText.addEventListener('change', listener);
        ref.current.append(richText);
        return () => {
          richText.removeEventListener('change', listener);
          richText.remove();
        };
      }
    }
    return () => {};
  }, [dataSource.doc, onChange, std, text, initializeCellIfNeeded]);

  return <div className={styles.richTextInput} ref={ref} style={style} />;
};

const DesktopRichTextCell = ({
  cell,
  dataSource,
  onChange,
  rowId,
}: DatabaseCellRendererProps) => {
  return (
    <PropertyValue>
      <RichTextInput
        cell={cell}
        dataSource={dataSource}
        onChange={onChange}
        rowId={rowId}
      />
    </PropertyValue>
  );
};

const MobileRichTextCell = ({
  cell,
  dataSource,
  onChange,
  rowId,
}: DatabaseCellRendererProps) => {
  const [open, setOpen] = useState(false);
  const name = useLiveData(cell.property.name$);
  return (
    <>
      <PropertyValue onClick={() => setOpen(true)}></PropertyValue>
      <ConfigModal
        onBack={() => setOpen(false)}
        open={open}
        onOpenChange={setOpen}
        title={
          <>
            <TextIcon />
            {name}
          </>
        }
      >
        <ConfigModal.RowGroup>
          <RichTextInput
            cell={cell}
            dataSource={dataSource}
            onChange={onChange}
            rowId={rowId}
            style={{ padding: 12 }}
          />
        </ConfigModal.RowGroup>
      </ConfigModal>
      <RichTextInput
        cell={cell}
        dataSource={dataSource}
        onChange={onChange}
        rowId={rowId}
      />
    </>
  );
};

export const RichTextCell = BUILD_CONFIG.isMobileEdition
  ? MobileRichTextCell
  : DesktopRichTextCell;
